import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, IconButton, Link } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Account, SegmentReportLine } from '../../interfaces/accounting.interface';
import {
    ExpandMode,
    TreeRow,
    buildReportTree,
    isGroupExpanded,
    mergePeriodLines,
    visibleRows,
} from '../../utils/reportTree';
import { variance, variancePercent } from '../../utils/reportPeriods';

/**
 * Shared building blocks of the income statement and balance sheet: account rows rendered as an
 * expandable tree with group subtotals, comparative columns (amount / variance / variance %) and a
 * drill-down link from every account to its ledger.
 */

export const statementHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

export const statementCellStyle = {
    padding: '8px 12px',
    borderBottom: '1px solid #eee',
};

export const formatAmount = (value: number | null | undefined) =>
    (value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatPercent = (v: number | null) => (v === null ? 'n/a' : `${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);

const varianceColor = (v: number) => (v === 0 ? undefined : v > 0 ? '#2e7d32' : '#c62828');

export interface ExpandState { mode: ExpandMode; overrides: Record<string, boolean> }

export const initialExpandState: ExpandState = { mode: 'default', overrides: {} };

/** Header cells for the figure columns: the current-period label, then 3 cells per comparative. */
export const FigureHeaders: React.FC<{ currentLabel: string; comparativeLabels: string[] }> = ({ currentLabel, comparativeLabels }) => (
    <>
        <th style={{ ...statementHeaderStyle, textAlign: 'right' }}>{currentLabel}</th>
        {comparativeLabels.map(label => (
            <React.Fragment key={label}>
                <th style={{ ...statementHeaderStyle, textAlign: 'right' }}>{label}</th>
                <th style={{ ...statementHeaderStyle, textAlign: 'right' }}>Variance</th>
                <th style={{ ...statementHeaderStyle, textAlign: 'right' }}>Var %</th>
            </React.Fragment>
        ))}
    </>
);

/** Figure cells for one row: values[0] is current, the rest are comparatives in header order. */
export const FigureCells: React.FC<{ values: number[]; bold?: boolean; color?: string }> = ({ values, bold, color }) => {
    const weight = bold ? 700 : undefined;
    return (
        <>
            <td style={{ ...statementCellStyle, textAlign: 'right', fontWeight: weight, color }}>{formatAmount(values[0])}</td>
            {values.slice(1).map((cmp, i) => {
                const v = variance(values[0], cmp);
                const pct = variancePercent(values[0], cmp);
                return (
                    <React.Fragment key={i}>
                        <td style={{ ...statementCellStyle, textAlign: 'right', fontWeight: weight }}>{formatAmount(cmp)}</td>
                        <td data-testid="variance" style={{ ...statementCellStyle, textAlign: 'right', fontWeight: weight, color: varianceColor(v) }}>{formatAmount(v)}</td>
                        <td data-testid="variance-pct" style={{ ...statementCellStyle, textAlign: 'right', fontWeight: weight, color: pct === null ? undefined : varianceColor(pct) }}>{formatPercent(pct)}</td>
                    </React.Fragment>
                );
            })}
        </>
    );
};

/** A label + figures row for totals and other non-account lines. */
export const TotalRow: React.FC<{ label: string; values: number[]; background?: string; color?: string; borderTop?: string; italic?: boolean; testId?: string }> =
    ({ label, values, background, color, borderTop, italic, testId }) => (
        <tr data-testid={testId} style={{ backgroundColor: background, fontWeight: italic ? 400 : 700, fontStyle: italic ? 'italic' : undefined, borderTop }}>
            <td style={statementCellStyle}>{label}</td>
            <FigureCells values={values} bold={!italic} color={color} />
        </tr>
    );

export const SectionLabelRow: React.FC<{ label: string; colSpan: number }> = ({ label, colSpan }) => (
    <tr>
        <td colSpan={colSpan} style={{ ...statementCellStyle, fontWeight: 600, fontSize: '0.9rem', color: '#555', borderBottom: 'none', paddingTop: 14 }}>{label}</td>
    </tr>
);

interface TreeRowsProps {
    /** Unique per segment block + section, so toggles in one section do not leak into another. */
    scope: string;
    /** The section's lines, one array per period (index 0 = current). */
    perPeriod: SegmentReportLine[][];
    accounts: Account[];
    state: ExpandState;
    onToggle: (scopedKey: string, currentlyExpanded: boolean) => void;
    /** Ledger URL for an account row. */
    ledgerHref: (accountId: string) => string;
}

export const StatementTreeRows: React.FC<TreeRowsProps> = ({ scope, perPeriod, accounts, state, onToggle, ledgerHref }) => {
    const rows = React.useMemo(
        () => buildReportTree(mergePeriodLines(perPeriod), accounts, perPeriod.length),
        [perPeriod, accounts],
    );
    const shown = visibleRows(rows, state.mode, state.overrides, scope);
    return (
        <>
            {shown.map((r: TreeRow, i) => {
                const expanded = r.group && isGroupExpanded(r, state.mode, state.overrides, scope);
                return (
                    <tr
                        key={r.key}
                        data-testid={`tree-row-${r.code}${r.direct ? '-direct' : ''}`}
                        style={{ backgroundColor: r.group ? '#f7f9fb' : i % 2 === 0 ? '#fff' : '#fafafa', fontWeight: r.group ? 600 : 400 }}
                    >
                        <td style={{ ...statementCellStyle, paddingLeft: 12 + r.depth * 20 }}>
                            {r.group ? (
                                <IconButton data-help="accounting.statementTree.toggle"
                                    size="small"
                                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${r.code}`}
                                    aria-expanded={expanded}
                                    onClick={() => onToggle(`${scope}:${r.key}`, expanded)}
                                    sx={{ mr: 0.5, p: 0.25 }}
                                >
                                    {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                </IconButton>
                            ) : (
                                <span style={{ display: 'inline-block', width: 26 }} />
                            )}
                            <span style={{ color: '#666', marginRight: 8 }}>{r.code}</span>
                            {r.group ? (
                                // A subtotal is not an account balance: only the account's own
                                // "(direct)" row and leaf rows drill down to the ledger.
                                <span>{r.name}</span>
                            ) : (
                                <Link component={RouterLink} to={ledgerHref(r.accountId)} underline="hover" color="inherit">
                                    {r.name}
                                </Link>
                            )}
                        </td>
                        <FigureCells values={r.totals} bold={r.group} />
                    </tr>
                );
            })}
        </>
    );
};

export const ExpandControls: React.FC<{ onExpandAll: () => void; onCollapseAll: () => void }> = ({ onExpandAll, onCollapseAll }) => (
    <>
        <Button data-help="accounting.statementTree.expandAll" size="small" variant="text" onClick={onExpandAll}>Expand all</Button>
        <Button data-help="accounting.statementTree.collapseAll" size="small" variant="text" onClick={onCollapseAll}>Collapse all</Button>
    </>
);

/** Reducer-free helpers for a page's expand state. */
export const expandAll = (): ExpandState => ({ mode: 'all', overrides: {} });
export const collapseAll = (): ExpandState => ({ mode: 'none', overrides: {} });
export const toggleKey = (s: ExpandState, scopedKey: string, currentlyExpanded: boolean): ExpandState =>
    ({ ...s, overrides: { ...s.overrides, [scopedKey]: !currentlyExpanded } });

export const ledgerLink = (accountId: string, from: string, to: string, segmentId?: string) =>
    `/accounting/accounts/${accountId}/ledger?from=${from}&to=${to}${segmentId ? `&segmentId=${segmentId}` : ''}`;
